/**
 * The case's conversations (plans/workbench2/06-chat-tab.md): the client's —
 * the family's one conversation, the internal notes merged in, as the case
 * page shows admins — and the partners', one per partner, the thread every
 * surface shares. Partners are listed by what they are to the case: in
 * conversation; on the case (locked on, or shortlisted for, an assignment);
 * one who could do the work (a candidate of an open partner-work ask, best
 * first); or another, from the directory. Choosing one opens their
 * conversation — the thread is made on the first message. From an open ask,
 * "Insert an offer" puts the work in the composer for the analyst to edit
 * and send; the partner chosen is approved in Setup. An evaluation copy has
 * no conversations: nothing leaves Hub for a copy.
 *
 * Laid out as Needs you is — the list beside the one open, the same panes
 * and rows — with the conversation headed as the triage step heads a
 * partner's chat: the name, what the partner is to the case, Manage, and
 * the offer to insert.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  Anchor,
  Badge,
  Button,
  Group,
  Loader,
  Menu,
  Select,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';

import { ChatPanel } from '@/components/Chat';
import { timeAgo } from '@/components/Inbox/timeAgo';
import { vnextApiClient } from '@/services/vnext-api';
import type { NetworkPartnerListItem } from '@/types/network-partner';
import type {
  WorkbenchConversations,
  WorkbenchHeader,
  WorkbenchOfferAsk,
  WorkbenchPartnerConversation,
} from '@/types/workbench';
import classes from './NeedsYou.module.css';

interface ConversationsViewProps {
  reportId: string;
  header: WorkbenchHeader;
  conversations: WorkbenchConversations | null;
  error: Error | null;
  /** The partner in focus (`partner` in the URL); null is the client's. */
  partnerId: string | null;
  onFocusPartner: (partnerId: string | null) => void;
  /** Open Setup, where the partner chosen is approved. */
  onOpenSetup?: () => void;
}

export type PartnerGroup = 'in_conversation' | 'on_case' | 'could' | 'directory';

const GROUPS: { key: PartnerGroup; label: string }[] = [
  { key: 'in_conversation', label: 'In conversation' },
  { key: 'on_case', label: 'On the case' },
  { key: 'could', label: 'Could do the work' },
  { key: 'directory', label: 'From the directory' },
];

/** What a partner is to the case, for the list's groups. */
export function groupOf(partner: WorkbenchPartnerConversation): PartnerGroup {
  if (partner.conversation) {
    return 'in_conversation';
  }
  if (partner.lockedOn.length > 0 || partner.shortlistedOn.length > 0) {
    return 'on_case';
  }
  return partner.candidateFor.length > 0 ? 'could' : 'directory';
}

export function partnerLabel(partner: WorkbenchPartnerConversation): string {
  return partner.name ?? partner.company ?? partner.email ?? 'A partner';
}

/** The record's terms and history, in a line. */
export function termsOf(partner: WorkbenchPartnerConversation): string | null {
  const parts: string[] = [];
  if (partner.fee) {
    parts.push(`${partner.currency ? `${partner.currency} ` : ''}${partner.fee}`);
  }
  if (partner.turnaroundDays != null) {
    parts.push(`about ${partner.turnaroundDays} days`);
  }
  const cases = partner.clientCases + partner.otherCases;
  if (cases > 0) {
    parts.push(
      `${cases} ${cases === 1 ? 'case' : 'cases'}${
        partner.clientCases > 0 ? ` (${partner.clientCases} for this client)` : ''
      }${partner.sameWork ? '' : ' — other work there'}`
    );
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** The offer an open ask makes, in the analyst's words to edit before it
 * is sent: the work, the subject, the country; the fee and timing asked. */
export function offerMessage(
  ask: WorkbenchOfferAsk,
  partnerName: string | null,
  subjectName: string | null
): string {
  const greeting = partnerName ? `Hi ${partnerName},` : 'Hi,';
  const where = ask.jurisdiction ? ` in ${ask.jurisdiction}` : '';
  const who = subjectName ? ` on ${subjectName}` : '';
  const lines =
    ask.components.length > 0
      ? ask.components.map((c) => `- ${c}${who}${where}`).join('\n')
      : `- (scope to be confirmed)${who}${where}`;
  return (
    `${greeting}\n\n` +
    `We have some work we'd like to offer you:\n\n` +
    `${lines}\n\n` +
    `Are you able to take this on? Please let us know your fee and timing here.\n\n` +
    `Thanks.`
  );
}

/** How an ask is named where several offers are on hand. */
function askLabel(ask: WorkbenchOfferAsk): string {
  const work = ask.components.length > 0 ? ask.components.join(', ') : 'Partner work';
  return ask.jurisdiction ? `${work} in ${ask.jurisdiction}` : work;
}

function fromDirectory(found: NetworkPartnerListItem): WorkbenchPartnerConversation {
  return {
    networkPartnerId: found.id,
    name: found.name,
    company: found.company,
    email: found.email,
    status: String(found.status),
    conversation: null,
    lockedOn: [],
    shortlistedOn: [],
    candidateFor: [],
    sameWork: false,
    fee: null,
    currency: null,
    turnaroundDays: null,
    clientCases: 0,
    otherCases: 0,
  };
}

const NO_CAPS = { textTransform: 'none' } as const;

/** What stands out about a partner: a reply waiting, locked, shortlisted,
 * a prospect. Nothing, where nothing does. */
function PartnerBadges({ partner }: Readonly<{ partner: WorkbenchPartnerConversation }>) {
  const badges: { label: string; color: string; variant: 'light' | 'outline' }[] = [];
  if (partner.conversation?.awaitingReply) {
    badges.push({ label: 'Reply waiting', color: 'orange', variant: 'light' });
  }
  if (partner.lockedOn.length > 0) {
    badges.push({ label: 'Locked', color: 'green', variant: 'light' });
  } else if (partner.shortlistedOn.length > 0) {
    badges.push({ label: 'Shortlisted', color: 'gray', variant: 'light' });
  }
  if (partner.status === 'prospect') {
    badges.push({ label: 'Prospect', color: 'gray', variant: 'outline' });
  }
  if (badges.length === 0) {
    return null;
  }
  return (
    <Group gap={4}>
      {badges.map((b) => (
        <Badge key={b.label} size="xs" variant={b.variant} color={b.color} style={NO_CAPS}>
          {b.label}
        </Badge>
      ))}
    </Group>
  );
}

/** A section of the list, named as Needs you names its sections. */
function SectionName({ name, count }: Readonly<{ name: string; count?: number }>) {
  return (
    <Group gap={6}>
      <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.04em' }}>
        {name}
      </Text>
      {count !== undefined && (
        <Badge size="xs" variant="light" color="gray">
          {count}
        </Badge>
      )}
    </Group>
  );
}

/** A conversation in the list: who it is with, when it last moved, what
 * stands out about it, and a line of detail. A Needs you row. */
function ConversationRow({
  label,
  at,
  badges,
  detail,
  selected,
  onSelect,
  testId,
}: Readonly<{
  label: string;
  at: string | null;
  badges?: React.ReactNode;
  detail: string | null;
  selected: boolean;
  onSelect: () => void;
  testId: string;
}>) {
  return (
    <UnstyledButton
      className={classes.row}
      aria-current={selected || undefined}
      onClick={onSelect}
      data-testid={testId}
    >
      <Stack gap={2}>
        <Group justify="space-between" wrap="nowrap" gap={8}>
          <Text size="sm" fw={500} truncate style={{ minWidth: 0 }}>
            {label}
          </Text>
          {at && (
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap', flex: 'none' }}>
              {at}
            </Text>
          )}
        </Group>
        {badges}
        {detail && (
          <Text size="xs" c="dimmed" truncate>
            {detail}
          </Text>
        )}
      </Stack>
    </UnstyledButton>
  );
}

/** The offer to insert from an open ask — one button, or a menu where
 * several asks are open — as the triage step's chat inserts its canned
 * offer. */
function OfferControl({
  asks,
  onInsert,
}: Readonly<{ asks: WorkbenchOfferAsk[]; onInsert: (ask: WorkbenchOfferAsk) => void }>) {
  if (asks.length === 0) {
    return null;
  }
  if (asks.length === 1) {
    const ask = asks[0];
    return (
      <Button
        size="compact-xs"
        variant="subtle"
        onClick={() => onInsert(ask)}
        data-testid={`offer-${ask.interventionId}`}
      >
        Insert an offer
      </Button>
    );
  }
  return (
    <Menu withinPortal position="bottom-end">
      <Menu.Target>
        <Button
          size="compact-xs"
          variant="subtle"
          rightSection={<IconChevronDown size={12} />}
          data-testid="offer-menu"
        >
          Insert an offer
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        {asks.map((ask) => (
          <Menu.Item
            key={ask.interventionId}
            onClick={() => onInsert(ask)}
            data-testid={`offer-${ask.interventionId}`}
          >
            {askLabel(ask)}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

function messagesOf(count: number | undefined): string {
  if (!count) {
    return 'No messages yet';
  }
  return `${count} ${count === 1 ? 'message' : 'messages'}`;
}

export function ConversationsView({
  reportId,
  header,
  conversations,
  error,
  partnerId,
  onFocusPartner,
  onOpenSetup,
}: Readonly<ConversationsViewProps>) {
  // Partners picked from the directory this session: listed until a first
  // message gives them a conversation of their own.
  const [added, setAdded] = useState<WorkbenchPartnerConversation[]>([]);
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<NetworkPartnerListItem[]>([]);
  const [searching, setSearching] = useState(false);
  const [seed, setSeed] = useState<string | undefined>(undefined);

  useEffect(() => {
    setSeed(undefined);
  }, [partnerId]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setFound([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setSearching(true);
      vnextApiClient
        .searchNetworkPartners({ q, limit: 10 })
        .then((items) => {
          if (!cancelled) {
            setFound(items);
          }
        })
        .catch(() => {
          if (!cancelled) {
            setFound([]);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setSearching(false);
          }
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  if (error && !conversations) {
    return (
      <Text size="sm" c="dimmed">
        The conversations couldn&apos;t be loaded.
      </Text>
    );
  }
  if (!conversations) {
    return <Loader size="sm" />;
  }
  if (conversations.evaluationCopy) {
    return (
      <Text size="sm" c="dimmed" data-testid="conversations-copy">
        An evaluation copy has no conversations: nothing leaves Hub for a copy.
      </Text>
    );
  }

  const partners = [...conversations.partners];
  for (const extra of added) {
    if (!partners.some((p) => p.networkPartnerId === extra.networkPartnerId)) {
      partners.push(extra);
    }
  }
  const selected = partnerId
    ? (partners.find((p) => p.networkPartnerId === partnerId) ?? null)
    : null;
  const subjectName = (ask: WorkbenchOfferAsk) =>
    header.subjects.find((s) => s.id === ask.subjectId)?.name ?? null;
  const pick = (id: string | null) => {
    const hit = found.find((f) => f.id === id);
    if (!hit) {
      return;
    }
    setAdded((prev) =>
      prev.some((p) => p.networkPartnerId === hit.id) ? prev : [...prev, fromDirectory(hit)]
    );
    setQuery('');
    onFocusPartner(hit.id);
  };
  const { client } = conversations;

  return (
    <div className={classes.panes} data-testid="conversations-view">
      <div className={classes.list}>
        <Stack gap="sm">
          <Stack gap={4}>
            <SectionName name="Client" />
            <ConversationRow
              label="The client"
              at={client?.lastMessageAt ? timeAgo(client.lastMessageAt) : null}
              badges={
                client?.awaitingReply && (
                  <Group gap={4}>
                    <Badge size="xs" variant="light" color="orange" style={NO_CAPS}>
                      Reply waiting
                    </Badge>
                  </Group>
                )
              }
              detail={messagesOf(client?.messages)}
              selected={selected === null}
              onSelect={() => onFocusPartner(null)}
              testId="conversation-client"
            />
          </Stack>
          {partners.length === 0 && (
            <Text size="xs" c="dimmed" px={6}>
              No partner is on the case yet. Sound one out from the directory.
            </Text>
          )}
          {GROUPS.map(({ key, label }) => {
            const members = partners.filter((p) => groupOf(p) === key);
            if (members.length === 0) {
              return null;
            }
            return (
              <Stack gap={4} key={key} data-testid={`conversations-group-${key}`}>
                <SectionName name={label} count={members.length} />
                {members.map((partner) => (
                  <ConversationRow
                    key={partner.networkPartnerId}
                    label={partnerLabel(partner)}
                    at={
                      partner.conversation?.lastMessageAt
                        ? timeAgo(partner.conversation.lastMessageAt)
                        : null
                    }
                    badges={<PartnerBadges partner={partner} />}
                    detail={termsOf(partner)}
                    selected={selected?.networkPartnerId === partner.networkPartnerId}
                    onSelect={() => onFocusPartner(partner.networkPartnerId)}
                    testId={`conversation-${partner.networkPartnerId}`}
                  />
                ))}
              </Stack>
            );
          })}
          <Select
            size="xs"
            searchable
            placeholder="Another partner…"
            aria-label="Another partner"
            data={found.map((f) => ({ value: f.id, label: f.name ?? f.email }))}
            searchValue={query}
            onSearchChange={setQuery}
            value={null}
            onChange={pick}
            nothingFoundMessage={
              searching
                ? 'Searching…'
                : query.trim().length < 2
                  ? 'Type a name'
                  : 'No partner found'
            }
            data-testid="conversations-add-partner"
          />
        </Stack>
      </div>
      <div className={classes.detail}>
        {selected ? (
          <Stack gap="xs" style={{ flex: 1, minHeight: 0 }} data-testid="conversation-partner">
            <Group justify="space-between" wrap="nowrap" align="flex-start">
              <Group gap="xs" style={{ minWidth: 0 }}>
                <Text size="sm" fw={600}>
                  {partnerLabel(selected)}
                </Text>
                <PartnerBadges partner={selected} />
                <Anchor
                  component={Link}
                  to={`/admin/network-partners/${selected.networkPartnerId}`}
                  size="xs"
                >
                  Manage
                </Anchor>
              </Group>
              <Group gap={6} wrap="nowrap">
                <OfferControl
                  asks={conversations.asks}
                  onInsert={(ask) =>
                    setSeed(offerMessage(ask, partnerLabel(selected), subjectName(ask)))
                  }
                />
                {conversations.asks.length > 0 && onOpenSetup && (
                  <Button
                    size="compact-xs"
                    variant="default"
                    onClick={onOpenSetup}
                    data-testid="conversations-open-setup"
                  >
                    Approve partner work in Setup
                  </Button>
                )}
              </Group>
            </Group>
            <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex' }}>
              <ChatPanel
                key={`np:${selected.networkPartnerId}`}
                entityType="report"
                entityId={reportId}
                kind="np_thread"
                npId={selected.networkPartnerId}
                enableAttachments
                seedText={seed}
              />
            </div>
          </Stack>
        ) : (
          <Stack gap="xs" style={{ flex: 1, minHeight: 0 }} data-testid="conversation-with-client">
            <Group gap="xs">
              <Text size="sm" fw={600}>
                The client
              </Text>
              {client?.awaitingReply && (
                <Badge size="xs" variant="light" color="orange" style={NO_CAPS}>
                  Reply waiting
                </Badge>
              )}
            </Group>
            <div style={{ flex: 1, minHeight: 0, minWidth: 0, display: 'flex' }}>
              <ChatPanel
                key="client"
                entityType="report"
                entityId={reportId}
                kind="client_thread"
                canSendPrivate
                mergeInternal
                enableAttachments
              />
            </div>
          </Stack>
        )}
      </div>
    </div>
  );
}
